/**
 * 페이앱·카드단말기·현금영수증·다수배분 — 내 신청 내역 · 새로 신청 · 취소(plan/driver-orders.md ④).
 * 상태는 회계팀이 적은 값(처리자·입금일자)이다. 다수배분은 기사가 취소할 수 없다(기사접수 앱과 같다).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverApi, driverError, type MyDriverOrder } from '../lib/driverApi';
import Screen, { Empty, ErrorBanner, HistoryHeading, HistoryTabs, Loading } from './Screen';
import { won } from './format';
import { ProxyTag } from './ProxyTag';

function statusOf(r: MyDriverOrder): { label: string; cls: string } {
  if (r.cancel_state) return { label: r.cancel_state === '취소' ? '취소신청' : '신청취소', cls: 'bg-red-50 text-red-700' };
  if (r.paid_date) return { label: `입금완료 ${r.paid_date}`, cls: 'bg-blue-50 text-blue-700' };
  if (r.issuer) return { label: '처리완료', cls: 'bg-green-50 text-green-700' };
  return { label: '처리 대기', cls: 'bg-canvas text-ink-soft' };
}
const KIND_ICON: Record<MyDriverOrder['kind'], string> = { 페이앱: '📱', 카드단말기: '💳', 현금영수증: '🧾', 다수배분: '🔀' };

/** `dasu` = 다수배분 탭(#226 — 내가 나눈 것 + 나에게 배분된 것만 따로) · 기본 탭은 다수배분을 뺀 3종. */
export default function MyOrders({ dasu = false }: { dasu?: boolean }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MyDriverOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    driverApi.listMyOrders()
      .then((r) => setRows(r.rows.filter((x) => (x.kind === '다수배분') === dasu)))
      .catch((e) => { setError(driverError(e, '신청 내역을 불러오지 못했습니다.')); setRows([]); });
  }, [nonce, dasu]);

  async function cancel(id: string) {
    setBusy(true); setError(null);
    try {
      await driverApi.cancelOrder({ id: Number(id), reason: reason.trim() || undefined });
      setCancelId(null); setReason(''); setNonce((n) => n + 1);
    } catch (e) { setError(driverError(e, '취소하지 못했습니다.')); } finally { setBusy(false); }
  }

  return (
    <Screen title={dasu ? '다수배분' : '페이앱·카드·현금영수증'} acting>
      <HistoryTabs active={dasu ? 'dasu' : 'orders'} />
      <button className="driver-btn mb-6" onClick={() => navigate(dasu ? '/orders/new?kind=다수배분' : '/orders/new')}>＋ {dasu ? '다수배분 신청하기' : '신청하기'}</button>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <HistoryHeading />
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>{dasu ? '다수배분 내역이 없습니다.' : '신청한 내역이 없습니다.'}</Empty> : (
        <ul className="space-y-4">
          {rows.map((r) => {
            const st = statusOf(r);
            const dists = r.lines.filter((l) => l.type !== '배분없음' && l.type !== '자체배분');
            return (
              <li key={r.order_id} className="driver-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-ink-soft">{KIND_ICON[r.kind]} {r.kind} · {r.order_no} · {r.request_date}</p>
                    <p className="mt-1 break-keep text-lg font-bold text-ink">{r.cust_name ?? (r.kind === '다수배분' ? `배분 ${dists.length}건` : '—')}</p>
                  </div>
                  <span className={`shrink-0 rounded-lg px-2 py-1 text-sm font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                <p className="mt-3 text-lg text-ink">{r.kind === '다수배분' ? '배분 합계' : '금액'} <b>{won(r.amount)}</b>원</p>
                {!r.mine && r.my_share && <p className="mt-1 text-base text-brand-700">나에게 배분된 금액 <b>{won(r.my_share)}</b>원</p>}
                {!r.mine && r.requester && <p className="mt-1 break-keep text-base text-ink-soft">보낸 사람 {r.requester}</p>}
                {r.mine && dists.length > 0 && (
                  <ul className="mt-2 space-y-1 text-base text-ink-soft">
                    {dists.map((d, i) => <li key={i} className={d.cancelled ? 'line-through' : ''}>{d.name} · {won(d.amount)}원</li>)}
                  </ul>
                )}
                {r.issuer && <p className="mt-1 text-base text-ink-soft">처리자 {r.issuer}</p>}
                <ProxyTag by={r.proxy_by} forName={r.proxy_for} />
                {r.mine && r.kind !== '다수배분' && !r.cancel_state && (
                  cancelId === r.order_id ? (
                    <div className="mt-4 space-y-3">
                      {r.paid_date && <p className="break-keep text-base text-ink-soft">입금이 확인된 신청입니다 — 취소를 신청하면 회계팀이 입금을 정리합니다.</p>}
                      <input className="driver-input" placeholder="취소 사유(선택)" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <div className="flex gap-3">
                        <button className="driver-btn bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => void cancel(r.order_id)}>
                          {r.issuer || r.paid_date ? '취소 신청' : '신청 취소'}
                        </button>
                        <button className="min-h-touch w-full rounded-xl border-2 border-line text-lg" onClick={() => setCancelId(null)}>닫기</button>
                      </div>
                    </div>
                  ) : (
                    <button className="mt-4 min-h-touch w-full rounded-xl border-2 border-line text-lg text-ink-soft" onClick={() => { setCancelId(r.order_id); setReason(''); }}>
                      취소하기
                    </button>
                  )
                )}
                {r.mine && r.kind === '다수배분' && !r.cancel_state && <p className="mt-3 text-base text-ink-soft">다수배분 취소는 회계팀에 연락해 주세요.</p>}
              </li>
            );
          })}
        </ul>
      )}
    </Screen>
  );
}
